import { useEffect, useState } from "react";
import axios from "axios";
import { Inbox, MessageCircle, Mail, Save, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { useSiteData } from "../../context/SiteData";
import { BookingDetailModal } from "./BookingDetailModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { id: "new", label: "New", cls: "bg-[var(--brand-soft)] text-[var(--brand)]" },
  { id: "confirmed", label: "Confirmed", cls: "bg-emerald-50 text-emerald-600" },
  { id: "completed", label: "Completed", cls: "bg-slate-100 text-slate-600" },
  { id: "cancelled", label: "Cancelled", cls: "bg-red-50 text-red-500" },
];

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const waLink = (phone, name, tour) => {
  const digits = (phone || "").replace(/[^\d]/g, "");
  const text = encodeURIComponent(
    `Hello ${name}, this is Voyage Switzerland regarding your booking${tour ? ` for ${tour}` : ""}.`
  );
  return `https://wa.me/${digits}?text=${text}`;
};

const BookingCard = ({ e, onSaved }) => {
  const [status, setStatus] = useState(e.status || "new");
  const [notes, setNotes] = useState(e.notes || "");
  const [busy, setBusy] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const save = async (patch) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/enquiries/${e.id}`, patch, { withCredentials: true });
      toast.success("Booking updated.");
      onSaved();
    } catch {
      toast.error("Could not update the booking.");
    } finally {
      setBusy(false);
    }
  };

  const st = STATUSES.find((s) => s.id === status) || STATUSES[0];

  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-5 shadow-[0_10px_30px_-16px_rgba(11,19,43,0.15)]" data-testid={`booking-row-${e.id}`}>
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3 min-w-0 flex-wrap">
          <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${st.cls}`} data-testid={`booking-status-${e.id}`}>
            {st.label}
          </span>
          <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${e.type === "enquiry" ? "bg-slate-100 text-slate-500" : "bg-[var(--brand-soft)] text-[var(--brand)]"}`}>
            {e.type === "tour_booking" ? "tour booking" : e.type === "group_quote" ? "group quote" : e.type}
          </span>
          <p className="font-display font-bold text-[#0b132b] truncate">{e.name}</p>
        </div>
        <p className="text-xs font-semibold text-slate-400">{fmt(e.created_at)}</p>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
        {e.tour && <span><span className="font-bold text-[#0b132b]">Tour:</span> {e.tour}</span>}
        {e.route && <span><span className="font-bold text-[#0b132b]">Route:</span> {e.route}</span>}
        {e.car && <span><span className="font-bold text-[#0b132b]">Car:</span> {e.car}</span>}
        {e.guests && <span><span className="font-bold text-[#0b132b]">Guests:</span> {e.guests}</span>}
        {e.pickup_date && <span><span className="font-bold text-[#0b132b]">Date:</span> {e.pickup_date}</span>}
        <span><span className="font-bold text-[#0b132b]">Email:</span> {e.email}</span>
        {e.phone && <span><span className="font-bold text-[#0b132b]">Phone:</span> {e.phone}</span>}
      </div>
      {e.message && <p className="mt-2.5 text-sm text-slate-500 leading-relaxed">{e.message}</p>}

      <div className="mt-4 flex flex-wrap items-center gap-2.5">
        <select
          data-testid={`booking-status-select-${e.id}`}
          value={status}
          onChange={(ev) => {
            setStatus(ev.target.value);
            save({ status: ev.target.value });
          }}
          disabled={busy}
          className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold outline-none transition focus:border-[var(--brand)]"
        >
          {STATUSES.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        {e.phone && (
          <a
            data-testid={`booking-whatsapp-${e.id}`}
            href={waLink(e.phone, e.name, e.tour)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 text-white text-xs font-bold px-4 py-2 transition-colors hover:bg-emerald-600"
          >
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </a>
        )}
        <a
          data-testid={`booking-email-${e.id}`}
          href={`mailto:${e.email}?subject=${encodeURIComponent(`Your Voyage Switzerland booking${e.tour ? `: ${e.tour}` : ""}`)}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#0b132b] text-white text-xs font-bold px-4 py-2 transition-colors hover:bg-slate-700"
        >
          <Mail className="w-3.5 h-3.5" /> Email
        </a>
        <button
          data-testid={`booking-ticket-${e.id}`}
          onClick={() => setDetailOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand)] text-white text-xs font-bold px-4 py-2 transition-colors hover:bg-[var(--brand-dark)]"
        >
          <FileText className="w-3.5 h-3.5" /> Details &amp; Ticket
        </button>
      </div>
      {detailOpen && (
        <BookingDetailModal
          booking={e}
          onClose={() => setDetailOpen(false)}
          onSaved={onSaved}
        />
      )}

      <div className="mt-4 flex gap-2">
        <input
          data-testid={`booking-notes-${e.id}`}
          value={notes}
          onChange={(ev) => setNotes(ev.target.value)}
          placeholder="Internal notes (only staff see this)…"
          className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium outline-none transition focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20"
        />
        <button
          data-testid={`booking-notes-save-${e.id}`}
          onClick={() => save({ notes })}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand)] text-white text-xs font-bold px-4 py-2 transition-colors hover:bg-[var(--brand-dark)] disabled:opacity-60"
        >
          <Save className="w-3.5 h-3.5" /> Save
        </button>
      </div>
    </div>
  );
};

export const BookingsTab = () => {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  useSiteData();

  const load = () =>
    axios
      .get(`${API}/enquiries`, { withCredentials: true })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));

  useEffect(() => {
    load();
  }, []);

  if (rows === null) {
    return <div className="w-10 h-10 rounded-full border-4 border-[var(--brand)]/20 border-t-[var(--brand)] animate-spin" data-testid="bookings-loading-spinner" />;
  }

  const visible = filter === "all" ? rows : rows.filter((r) => (r.status || "new") === filter);

  return (
    <div data-testid="admin-bookings-tab">
      <div className="flex flex-wrap items-center gap-2.5 mb-5 justify-between">
        <div className="flex flex-wrap gap-2.5" data-testid="bookings-filters">
          {[{ id: "all", label: "All" }, ...STATUSES].map((s) => (
            <button
              key={s.id}
              data-testid={`bookings-filter-${s.id}`}
              onClick={() => setFilter(s.id)}
              className={`rounded-full px-4 py-2 text-xs font-bold transition-all duration-300 ${
                filter === s.id
                  ? "bg-[var(--brand)] text-white shadow-lg shadow-[var(--brand)]/30"
                  : "bg-white border border-slate-200 text-slate-600 hover:border-[var(--brand)] hover:text-[var(--brand)]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <button
          data-testid="bookings-export-csv"
          onClick={async () => {
            try {
              const r = await axios.get(`${API}/enquiries/export`, { withCredentials: true, responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a");
              a.href = url; a.download = `voyage-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
              document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
            } catch { toast.error("Export failed."); }
          }}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#0b132b] text-white text-xs font-bold px-4 py-2 hover:bg-[#0b132b]/90"
        >
          <Download className="w-3.5 h-3.5" /> Export Excel / CSV
        </button>
      </div>

      {visible.length === 0 ? (
        <div className="rounded-3xl bg-white border border-slate-100 p-14 text-center" data-testid="bookings-empty-state">
          <Inbox className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="mt-4 font-display font-bold text-[#0b132b]">Nothing here yet</p>
          <p className="mt-1 text-sm text-slate-400">Bookings and inquiries will appear here.</p>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="admin-bookings-list">
          {visible.map((e) => (
            <BookingCard key={e.id} e={e} onSaved={load} />
          ))}
        </div>
      )}
    </div>
  );
};
